import type { LocaleCode, Translate } from '@client-app/i18n';
import { VerifyAuthResponse, WorkspacePlan } from '@common/api/authService';
import { WorkspaceSubscriptionSummary } from '@common/api/billingService';
import { formatAutoRenewText, formatPlanDate, formatTrialExpirationText } from './planTextUtils';

type PlanTextStatus = 'trial' | 'active' | 'canceled' | 'expired' | 'none';

interface BuildUserBillingPlanTextOptions {
    summary: WorkspaceSubscriptionSummary | null;
    userPlan: WorkspacePlan | null;
    now?: Date;
    isExpired?: boolean;
    isTrial?: boolean;
    locale?: LocaleCode;
    t?: Translate;
}

interface UserBillingPlanText {
    planDisplayText: string;
    badge: string;
    expirationText: string;
    buttonText: string;
    status: PlanTextStatus;
}

const MS_PER_DAY = 1000 * 60 * 60 * 24;

const getDaysLeft = (endAt: string, now: Date): number => {
    return Math.max(0, Math.ceil((new Date(endAt).getTime() - now.getTime()) / MS_PER_DAY));
};

const isPendingCancellation = (
    summary: WorkspaceSubscriptionSummary | null,
    userPlan: WorkspacePlan | null,
    isExpired: boolean,
    isTrial: boolean,
): boolean => {
    if (isExpired || isTrial) return false;

    return Boolean(
        summary?.status === 'pending_cancellation'
        || userPlan?.status === 'pending_cancellation'
        || userPlan?.cancelAtPeriodEnd
        || summary?.isAutoRenew === false
    );
};

const isTrialStatus = (status?: string | null): boolean => {
    const normalized = (status || '').toLowerCase();
    return normalized === 'trial' || normalized === 'trialing';
};

export const resolveUserBillingIsTrial = (
    summary: WorkspaceSubscriptionSummary | null,
    userPlan: WorkspacePlan | null,
    now: Date = new Date(),
): boolean => {
    if (summary?.isTrial !== undefined) {
        return Boolean(summary.isTrial || isTrialStatus(summary.status));
    }

    if (isTrialStatus(summary?.status) || isTrialStatus(userPlan?.status)) {
        return true;
    }

    return Boolean(userPlan?.trialEndAt && new Date(userPlan.trialEndAt).getTime() > now.getTime());
};

const translatePlanAction = (value: string, t?: Translate): string => {
    if (!t) return value;

    switch (value.trim().toLowerCase()) {
        case 'start subscription':
            return t('website.planText.startSubscription');
        case 'upgrade':
            return t('website.planText.upgrade');
        case 'renew':
            return t('website.planText.renew');
        case 'manage':
            return t('website.planText.manage');
        default:
            return value;
    }
};

const translatePlanBadge = (value: string, t?: Translate): string => {
    if (!t) return value;

    switch (value.trim().toLowerCase()) {
        case 'trial':
            return t('website.planText.trial');
        case 'expired':
            return t('website.planText.expired');
        default:
            return value;
    }
};

export const buildUserBillingPlanText = ({
    summary,
    userPlan,
    now = new Date(),
    isExpired: isExpiredOverride,
    isTrial: isTrialOverride,
    locale = 'en',
    t,
}: BuildUserBillingPlanTextOptions): UserBillingPlanText => {
    const nowTimestamp = now.getTime();
    const summaryExpiredAt = summary?.expiresAt ? new Date(summary.expiresAt).getTime() : null;
    const legacyExpired = Boolean(userPlan?.currentPeriodEnd && nowTimestamp > new Date(userPlan.currentPeriodEnd).getTime());
    const isExpired = isExpiredOverride ?? (
        summary?.status === 'expired'
        || summary?.status === 'canceled'
        || Boolean(summaryExpiredAt && nowTimestamp > summaryExpiredAt)
        || legacyExpired
    );
    const isTrial = isTrialOverride ?? resolveUserBillingIsTrial(summary, userPlan, now);
    const isCanceled = isPendingCancellation(summary, userPlan, isExpired, isTrial);

    if (summary) {
        const filterStatusLabel = (v?: string | null) => v && v.toLowerCase() !== 'plan expired' ? v : '';
        const summaryPlan =
            summary.planName
            || userPlan?.planName
            || filterStatusLabel(summary.title)
            || filterStatusLabel(summary.statusLabel)
            || (t ? t('website.planText.basic') : 'Basic');
        const summaryStatus = (summary.status || '').toLowerCase();
        const nextBillingAt = summary.nextBillingAt || userPlan?.currentPeriodEnd;
        const expiresAt = summary.expiresAt || userPlan?.currentPeriodEnd;
        const trialEndsAt = userPlan?.trialEndAt || summary.expiresAt || summary.nextBillingAt;
        const status: PlanTextStatus = isExpired || summaryStatus === 'expired' || summaryStatus === 'canceled'
            ? 'expired'
            : isTrial
                ? 'trial'
                : isCanceled
                    ? 'canceled'
                    : 'active';

        const rawButtonText = status === 'trial'
            ? 'Start subscription'
            : status === 'expired'
                ? isTrial ? 'Start subscription' : 'Upgrade'
                : summary.actionLabel
                    || summary.primaryAction
                    || (status === 'canceled' ? 'Renew' : 'Manage');
        const buttonText = translatePlanAction(rawButtonText, t);

        let expirationText = '';
        if (status === 'expired' && isTrial && (trialEndsAt || expiresAt)) {
            expirationText = t
                ? t('website.planText.freeTrialExpiredOn', { date: formatPlanDate(trialEndsAt || expiresAt!, locale) })
                : `Free trial expired on ${formatPlanDate(trialEndsAt || expiresAt!, locale)}.`;
        } else if (status === 'expired' && expiresAt) {
            expirationText = t
                ? t('website.planText.expiredOn', { date: formatPlanDate(expiresAt, locale) })
                : `Expired on ${formatPlanDate(expiresAt, locale)}.`;
        } else if (isTrial && trialEndsAt) {
            expirationText = formatTrialExpirationText(trialEndsAt, now, locale, t);
        } else if (status === 'canceled' && expiresAt) {
            const daysLeft = getDaysLeft(expiresAt, now);
            expirationText = daysLeft < 7
                ? (t ? t('website.planText.daysLeft', { days: daysLeft }) : `${daysLeft} days left.`)
                : (t ? t('website.planText.expiresOn', { date: formatPlanDate(expiresAt, locale) }) : `Expires on ${formatPlanDate(expiresAt, locale)}.`);
        } else if (status === 'active' && nextBillingAt) {
            expirationText = formatAutoRenewText(nextBillingAt, locale, t);
        } else if (summary.supportingText || summary.statusMessage) {
            expirationText = summary.supportingText || summary.statusMessage || '';
        } else if (nextBillingAt) {
            expirationText = formatAutoRenewText(nextBillingAt, locale, t);
        }

        return {
            planDisplayText: summaryPlan,
            badge: translatePlanBadge(
                (isTrial && status === 'trial') ? 'Trial' : (status === 'expired' ? 'Expired' : (summary.badge || '')),
                t,
            ),
            expirationText,
            buttonText,
            status,
        };
    }

    if (!userPlan) {
        return {
            planDisplayText: '',
            badge: '',
            expirationText: '',
            buttonText: t ? t('website.planText.manage') : 'Manage',
            status: 'none',
        };
    }

    if (isExpired) {
        const expiredDate = userPlan.trialEndAt || userPlan.currentPeriodEnd;
        return {
            planDisplayText: userPlan.planName || (t ? t('website.planText.basic') : 'Basic'),
            badge: t ? t('website.planText.expired') : 'Expired',
            expirationText: isTrial && expiredDate
                ? (t ? t('website.planText.freeTrialExpiredOn', { date: formatPlanDate(expiredDate, locale) }) : `Free trial expired on ${formatPlanDate(expiredDate, locale)}.`)
                : userPlan.currentPeriodEnd
                    ? (t ? t('website.planText.expiredOn', { date: formatPlanDate(userPlan.currentPeriodEnd, locale) }) : `Expired on ${formatPlanDate(userPlan.currentPeriodEnd, locale)}.`)
                    : '',
            buttonText: t
                ? t(isTrial ? 'website.planText.startSubscription' : 'website.planText.upgrade')
                : (isTrial ? 'Start subscription' : 'Upgrade'),
            status: 'expired',
        };
    }

    if (isTrial) {
        return {
            planDisplayText: userPlan.planName || (t ? t('website.planText.basic') : 'Basic'),
            badge: t ? t('website.planText.trial') : 'Trial',
            expirationText: formatTrialExpirationText(userPlan.trialEndAt || userPlan.currentPeriodEnd || null, now, locale, t),
            buttonText: t ? t('website.planText.startSubscription') : 'Start subscription',
            status: 'trial',
        };
    }

    if (isCanceled && userPlan.currentPeriodEnd) {
        const daysLeft = getDaysLeft(userPlan.currentPeriodEnd, now);
        return {
            planDisplayText: userPlan.planName || (t ? t('website.planText.basic') : 'Basic'),
            badge: '',
            expirationText: daysLeft < 7
                ? (t ? t('website.planText.daysLeft', { days: daysLeft }) : `${daysLeft} days left.`)
                : (t ? t('website.planText.expiresOn', { date: formatPlanDate(userPlan.currentPeriodEnd, locale) }) : `Expires on ${formatPlanDate(userPlan.currentPeriodEnd, locale)}.`),
            buttonText: t ? t('website.planText.renew') : 'Renew',
            status: 'canceled',
        };
    }

    return {
        planDisplayText: userPlan.planName || (t ? t('website.planText.basic') : 'Basic'),
        badge: '',
        expirationText: userPlan.currentPeriodEnd ? formatAutoRenewText(userPlan.currentPeriodEnd, locale, t) : '',
        buttonText: t ? t('website.planText.manage') : 'Manage',
        status: 'active',
    };
};
